import { Link as RouterLink, Outlet, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { Box, BottomNavigation, BottomNavigationAction, Link, Paper, Stack, Typography } from '@mui/material';
import AIAssistantIcon from '@mui/icons-material/AutoAwesome';
import SchoolIcon from '@mui/icons-material/School';
import ReportingIcon from '@mui/icons-material/BarChart';
import AppBar from '../AppBar';
import PendingInviteBanner from '../PendingInviteBanner';
import WelcomeVideoDialog from '../WelcomeVideoDialog';
import SubscriptionBanner from '../SubscriptionBanner';
import VerticalSidebar from './VerticalSidebar';
import { BookmarkProvider } from '../../contexts/BookmarkContext';

function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = [
    { label: 'Assistant', path: '/app/assistant', icon: <AIAssistantIcon /> },
    { label: 'Learning', path: '/app/courses', icon: <SchoolIcon /> },
    { label: 'Reporting', path: '/app/cpe-history', icon: <ReportingIcon /> },
  ];

  const currentTab = tabs.findIndex(t =>
    location.pathname === t.path || location.pathname.startsWith(t.path + '/')
  );

  return (
    <Paper
      elevation={3}
      sx={{ display: { xs: 'block', sm: 'none' }, position: 'sticky', bottom: 0, zIndex: 1100 }}
    >
      <BottomNavigation
        value={currentTab === -1 ? false : currentTab}
        onChange={(_e, idx) => navigate(tabs[idx].path)}
        showLabels
      >
        {tabs.map(t => (
          <BottomNavigationAction key={t.path} label={t.label} icon={t.icon} />
        ))}
      </BottomNavigation>
    </Paper>
  );
}

/**
 * Routes that should run edge-to-edge without the bottom footer.
 * Chat-style or full-height surfaces where a footer would create a
 * second band of borders/backgrounds directly under their input bar.
 */
const FULL_BLEED_ROUTES = ['/app/assistant'];

export default function MainLayout() {
  const location = useLocation();
  const isFullBleed = FULL_BLEED_ROUTES.some((p) => matchPath({ path: p, end: true }, location.pathname));

  return (
    <BookmarkProvider>
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Ascend Platform AppBar Header */}
      <AppBar />

      {/* Subscription upsell banner — shown only when user has no active subscription */}
      <SubscriptionBanner />

      {/* Pending tenant invitation dialog */}
      <PendingInviteBanner />

      {/* One-time welcome video for newly created accounts */}
      <WelcomeVideoDialog />

      {/* Main Content Area with Sidebar */}
      <Box sx={{ display: 'flex', flexGrow: 1, height: 0 }}>
        {/* Vertical Sidebar Navigation — hidden on xs, visible sm+ */}
        <VerticalSidebar />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            py: { xs: 3, sm: 4 },
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ px: { xs: 2, sm: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Outlet />
          </Box>
        </Box>
      </Box>

      {/* Mobile Bottom Navigation — visible xs only */}
      <MobileBottomNav />

      {/* Footer — same white surface as AppBar (they bookend the app shell).
          Content spans the full footer width: © pinned to the left edge,
          links pinned to the right edge, matching the AppBar's full-width layout.
          Hidden on xs (mobile bottom nav serves) and on full-bleed routes (chat). */}
      {!isFullBleed && (
        <Box
          component="footer"
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            flexWrap: 'wrap',
            rowGap: 1,
            mt: 'auto',
            py: 2.5,
            px: { xs: 2, sm: 3 },
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}
          >
            &copy; {new Date().getFullYear()} CPAx Academy
          </Typography>
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{
              fontSize: '0.8125rem',
              '& a': {
                color: 'text.secondary',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'color 0.15s ease',
                '&:hover': { color: 'primary.main', textDecoration: 'underline' },
              },
            }}
          >
            <Link component={RouterLink} to="/app/faq" sx={{ fontSize: 'inherit' }}>
              Help &amp; FAQ
            </Link>
            <Link component={RouterLink} to="/app/contact" sx={{ fontSize: 'inherit' }}>
              Contact
            </Link>
            <Link component={RouterLink} to="/app/release-notes" sx={{ fontSize: 'inherit' }}>
              Release Notes
            </Link>
          </Stack>
        </Box>
      )}

    </Box>
    </BookmarkProvider>
  );
}
